import { Component, computed, effect, inject, input, output, DestroyRef, signal } from '@angular/core';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { DiscountType, DsmDealOptionProduct } from '../../../models/dsm-deal-options-view.models';
import { CurrencyService, CurrencyInputFieldComponent, CurrencyFormatPipe } from '@zayan-erp/shared';
import { DsmDealOptionsService } from '../../../services/dsm-deal-options.service';
import { ButtonModule } from '@openng/optimus-ui/button';
import { SelectButtonModule } from '@openng/optimus-ui/selectbutton';
import { InputNumberModule } from '@openng/optimus-ui/inputnumber';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { debounceTime, tap } from 'rxjs';
import { FloatLabelModule } from '@openng/optimus-ui/floatlabel';

@Component({
  selector: 'dsm-deal-pricing-discount',
  imports: [
    FormsModule,
    ReactiveFormsModule,
    CurrencyInputFieldComponent,
    ButtonModule,
    SelectButtonModule,
    InputNumberModule,
    CurrencyFormatPipe,
    FloatLabelModule
  ],
  templateUrl: './dsm-deal-pricing-discount.component.html',
  standalone: true
})
export class DsmDealPricingDiscountComponent {
  #currencyService = inject(CurrencyService);
  currencySymbol = computed(() => this.#currencyService?.currencySymbol() ?? 'CAD');
  buyerAndBuilderApproved = input<boolean>(false);
  discountUpdated = output<void>();
  addendumOption = input.required<DsmDealOptionProduct>();
  isCancelled = input.required<boolean>();
  #dealOptionsService = inject(DsmDealOptionsService);
  #destroyRef = inject(DestroyRef);
  incentive: number = 0;

  discountType = DiscountType;

  discountTypeOptions = [
    { label: 'Amount', value: DiscountType.Amount },
    { label: 'Percentage', value: DiscountType.Percentage }
  ];

  discountForm = new FormGroup({
    discountType: new FormControl<DiscountType>(DiscountType.Amount),
    discountValue: new FormControl<number>(0, [Validators.min(0)])
  });
  incentiveForm = new FormGroup({
    incentive: new FormControl(0, [Validators.min(0)])
  });

  typedValue = signal<number | null>(null);
  readonly savedVisible = signal(0);

  derivedPercentageDisplay = computed(() => {
    const { discountPercentage, amount } = this.addendumOption();
    const typed = this.typedValue();
    if (typed === null) return discountPercentage.toFixed(2);
    return amount > 0 ? ((typed / amount) * 100).toFixed(2) : '0.00';
  });

  derivedAmountDisplay = computed(() => {
    const { discount, amount } = this.addendumOption();
    const typed = this.typedValue();
    return typed === null ? discount.toFixed(2) : ((typed / 100) * amount).toFixed(2);
  });

  constructor() {
    effect(() => {
      const addendumOption = this.addendumOption();
      this.incentiveForm.patchValue(
        {
          incentive: addendumOption.incentiveAmount
        },
        { emitEvent: false }
      );
      this.incentiveForm.controls.incentive.setValidators([Validators.max(addendumOption.amount)]);

      const discountType = addendumOption.discountTypeId ?? DiscountType.Amount;
      const discountValue = addendumOption.discountValue ?? addendumOption.discount;
      this.discountForm.patchValue({ discountType, discountValue }, { emitEvent: false });
      this.applyDiscountValidators(discountType);

      this.typedValue.set(null);
    });

    this.incentiveForm.valueChanges.pipe(debounceTime(750), takeUntilDestroyed(this.#destroyRef)).subscribe(() => {
      if (this.incentiveForm.valid && !this.#matchesSavedIncentive()) {
        this.onIncentiveSubmit();
      }
    });

    this.discountForm.controls.discountType.valueChanges.pipe(takeUntilDestroyed(this.#destroyRef)).subscribe(type => {
      this.applyDiscountValidators(type ?? DiscountType.Amount);
      this.discountForm.controls.discountValue.setValue(0, { emitEvent: false });
      this.typedValue.set(0);
    });

    this.discountForm.controls.discountValue.valueChanges
      .pipe(
        tap(val => this.typedValue.set(val ?? 0)),
        debounceTime(750),
        takeUntilDestroyed(this.#destroyRef)
      )
      .subscribe(() => {
        if (this.discountForm.valid && !this.#matchesSavedDiscount()) this.onDiscountSubmit();
      });
  }

  /** True when the form already matches what's persisted, so submitting would be a no-op request. */
  #matchesSavedDiscount(): boolean {
    const option = this.addendumOption();
    const savedType = option.discountTypeId ?? DiscountType.Amount;
    const savedValue = option.discountValue ?? option.discount ?? 0;
    return (
      (this.discountForm.value.discountType ?? DiscountType.Amount) === savedType &&
      (this.discountForm.value.discountValue ?? 0) === savedValue
    );
  }

  #matchesSavedIncentive(): boolean {
    return (this.incentiveForm.value.incentive ?? 0) === (this.addendumOption().incentiveAmount ?? 0);
  }

  applyDiscountValidators(type: DiscountType) {
    this.discountForm.controls.discountValue.setValidators(
      type === DiscountType.Percentage ? [Validators.min(0), Validators.max(100)] : [Validators.min(0)]
    );
    this.discountForm.controls.discountValue.updateValueAndValidity({ emitEvent: false });
  }

  onIncentiveSubmit() {
    this.#dealOptionsService
      .updateIncentive(this.addendumOption().id, { incentiveAmount: this.incentiveForm.value.incentive ?? 0 }, false)
      .subscribe(() => {
        this.savedVisible.update(n => n + 1);
        this.discountUpdated.emit();
      });
  }

  onDiscountSubmit() {
    const discountTypeId = this.discountForm.value.discountType ?? DiscountType.Amount;
    const discountValue = this.discountForm.value.discountValue ?? 0;
    this.#dealOptionsService
      .updateDiscount(this.addendumOption().id, { discountTypeId, discountValue }, false)
      .subscribe(() => {
        this.savedVisible.update(n => n + 1);
        this.discountUpdated.emit();
      });
  }

  // calculateDiscountPercentage() {
  //   const discountAmountValue = this.discountForm.value.discountAmount ?? 0;
  //   const addendumOption = this.addendumOption();

  //   const percentage = (Number(discountAmountValue) / addendumOption.amount) * 100;
  //   this.discountForm.controls.discountPercentage.setValue(Number(percentage.toFixed(2)), { emitEvent: false });
  // }

  // calculateDiscountAmount() {
  //   const discountPercentageValue = this.discountForm.value.discountPercentage ?? 0;
  //   const addendumOption = this.addendumOption();
  //   const discountAmount = addendumOption.amount * (Number(discountPercentageValue) / 100);
  //   this.discountForm.controls.discountAmount.setValue(Number(discountAmount.toFixed(2)), { emitEvent: false });
  // }
}
